import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, Lock, AlertTriangle, Check } from "lucide-react";
import { API } from "@/lib/api";
import { DICT, missingText, shownSteps } from "@/lib/i18n";
import { DownstreamStep } from "@/pages/portal/PortalDownstream";
import { cn } from "@/lib/utils";
import { Btn, CompanyStep, DdsStep, DocsStep, MaterialsStep, PlotsStep, inputCls } from "@/pages/portal/PortalSteps";
import PortalMillRefs from "@/pages/portal/PortalMillRefs";
import { Changes, ConfirmBar, EmailLink, HelpTip, PrefillOffer, Progress } from "@/pages/portal/PortalExtras";

const ERR = (e, t) => {
  const d = e?.response?.data?.detail;
  if (d === "file_too_large") return t.file_too_large;
  if (d === "file_type") return t.file_type;
  return typeof d === "string" ? d : t.error;
};

function Header({ lang, setLang, t, saving, savedAt }) {
  return (
    <header className="sticky top-0 z-[1000] border-b border-white/10 bg-[#1C1C1E]">
      <div className="mx-auto flex max-w-4xl items-center justify-between gap-3 px-4 py-3">
        <img src="/logo-epg-mark.png" alt="EP Group" className="h-7 w-auto" data-testid="portal-logo" />
        <div className="flex items-center gap-3">
          {savedAt !== undefined && <span className="hidden text-xs text-slate-400 sm:inline" data-testid="portal-save-state">{saving ? t.saving : savedAt ? `${t.saved} ${savedAt}` : ""}</span>}
          <div className="flex rounded-lg bg-white/10 p-0.5" role="group" aria-label={t.lang}>
            {["en", "fr"].map((l) => (
              <button key={l} data-testid={`portal-lang-${l}`} onClick={() => setLang(l)} className={cn("rounded-md px-2.5 py-1 text-xs font-bold uppercase", lang === l ? "bg-[#D52B3B] text-white" : "text-slate-300")}>{l}</button>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}

export default function Portal() {
  const { token } = useParams();
  const [st, setSt] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [lang, setLang] = useState("en");
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [submitErr, setSubmitErr] = useState(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sign, setSign] = useState("");
  const timers = useRef({});
  const inited = useRef(false);
  const t = DICT[lang];
  const base = `${API}/portal/${token}`;

  const load = useCallback(async (first, keepData) => {
    try {
      const r = await axios.get(base);
      setSt((cur) => (keepData && cur ? { ...r.data, data: cur.data } : r.data));
      if (first && !inited.current) {
        inited.current = true;
        setLang(r.data.language || "en");
        const sh = shownSteps(r.data.data.company?.operator_status);
        const s0 = Math.min(Math.max(r.data.data.step || 1, 1), 7);
        setStep(sh.includes(s0) ? s0 : sh.find((x) => x > s0) || 7);
        setSign(r.data.data.signed_name || "");
      }
    } catch {
      setInvalid(true);
    }
  }, [base]);
  useEffect(() => { load(true); }, [load]);

  const pending = useRef({});
  const save = (section, valOrFn, delay = 700) => {
    setSt((s) => {
      const value = typeof valOrFn === "function" ? valOrFn(s.data[section]) : valOrFn;
      pending.current[section] = value;
      return { ...s, data: { ...s.data, [section]: value } };
    });
    clearTimeout(timers.current[section]);
    timers.current[section] = setTimeout(async () => {
      setSaving(true);
      try {
        await axios.put(`${base}/data`, { section, value: pending.current[section] });
        setSavedAt(new Date().toLocaleTimeString(lang === "fr" ? "fr-FR" : "en-GB", { hour: "2-digit", minute: "2-digit" }));
        if (section !== "step") load(false, true);
      } finally { setSaving(false); }
    }, delay);
  };
  const go = (n) => { setStep(n); window.scrollTo({ top: 0, behavior: "smooth" }); if (!st.read_only) save("step", n, 0); };

  const api = {
    addPlot: async (body) => { try { await axios.post(`${base}/plots`, body); await load(); return {}; } catch (e) { return { error: ERR(e, t) }; } },
    deletePlot: async (id) => { await axios.delete(`${base}/plots/${id}`); load(); },
    uploadPlots: async (f, mats) => { const fd = new FormData(); fd.append("file", f); fd.append("materials", mats.join(",")); try { const r = await axios.post(`${base}/plots/upload`, fd); load(); return r.data; } catch (e) { return { error: ERR(e, t) }; } },
    uploadDoc: async (f, type, expiry) => { const fd = new FormData(); fd.append("file", f); fd.append("doc_type", type); fd.append("expiry", expiry); try { await axios.post(`${base}/documents`, fd); load(); return {}; } catch (e) { return { error: ERR(e, t) }; } },
    deleteDoc: async (id) => { await axios.delete(`${base}/documents/${id}`); load(); },
  };

  const submit = async () => {
    setBusy(true);
    setSubmitErr(null);
    try {
      await axios.post(`${base}/submit`, { declaration: !!st.data.declaration, signed_name: sign });
      setDone(true);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setSubmitErr(d?.missing || [{ key: "error" }]);
    } finally { setBusy(false); }
  };

  if (invalid)
    return (
      <div className="min-h-screen bg-slate-50" data-testid="portal-invalid">
        <Header lang={lang} setLang={setLang} t={t} />
        <div className="mx-auto max-w-md px-6 py-24 text-center">
          <Lock className="mx-auto h-10 w-10 text-slate-300" />
          <h1 className="mt-4 text-2xl font-semibold text-slate-900">{t.invalid_title}</h1>
          <p className="mt-2 text-slate-600" data-testid="portal-invalid-message">{t.invalid}</p>
        </div>
      </div>
    );
  if (!st) return <div className="flex h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const ro = st.read_only;
  const d = st.data;
  const missing = st.missing.filter((m) => !(m.key === "declaration" && d.declaration));
  const shown = shownSteps(d.company?.operator_status);
  const idx = shown.indexOf(step);
  const prev = shown[idx - 1], nxt = shown[idx + 1];
  return (
    <div className="min-h-screen bg-slate-50 pb-16" data-testid="portal-page">
      <Header lang={lang} setLang={setLang} t={t} saving={saving} savedAt={savedAt || ""} />
      <main className="mx-auto max-w-4xl px-4 py-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D52B3B]">{t.portal}</p>
        <h1 className="mt-1 text-2xl font-semibold text-slate-900 sm:text-3xl" data-testid="portal-supplier-name">{st.supplier.name}</h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-600">{t.intro}</p>
        <p className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">{t.valid_until} {new Date(st.expires_at).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB")}{st.has_email && !ro && <EmailLink t={t} base={base} />}</p>
        {!ro && st.prefill?.status === "offered" && <PrefillOffer t={t} pf={st.prefill} onChoose={async (c) => { await axios.post(`${base}/prefill`, { choice: c }); await load(); }} />}
        {!ro && !done && <Progress t={t} missing={missing} saving={saving} savedAt={savedAt} shown={shown} />}
        {(ro || done) && (
          <div className="mt-4 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-900" data-testid="portal-readonly-banner">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
            <div>{done && <p className="font-semibold" data-testid="portal-thanks">{t.thanks}</p>}<p className="text-sm">{t.read_only}</p></div>
          </div>
        )}
        {st.changes_comment && !ro && (
          <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-900" data-testid="portal-changes-comment">
            <p className="font-semibold">{t.changes_requested}</p><p className="mt-1 whitespace-pre-wrap text-sm">{st.changes_comment}</p>
          </div>
        )}
        <nav className="scroll-thin -mx-4 mt-6 flex gap-2 overflow-x-auto px-4 pb-2" data-testid="portal-stepper">
          {shown.map((n) => (
            <button key={n} data-testid={`portal-step-${n}`} onClick={() => go(n)}
              className={cn("flex max-w-[16rem] shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium", step === n ? "border-[#1C1C1E] bg-[#1C1C1E] text-white" : "border-slate-300 bg-white text-slate-600")}>
              <span className={cn("flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px]", step === n ? "bg-[#D52B3B]" : "bg-slate-100")}>{n}</span><span className="truncate">{t.steps[n - 1]}</span>
            </button>
          ))}
        </nav>
        <section className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
          <h2 className="mb-5 text-lg font-semibold text-slate-900" data-testid="portal-step-title">{t.step} {step} · {t.steps[step - 1]}</h2>
          {!ro && <ConfirmBar t={t} pf={st.prefill} step={step} onConfirm={async (k) => { await axios.post(`${base}/confirm`, { step: k }); load(false, true); }} />}
          <fieldset disabled={ro} className="min-w-0">
            {step === 1 && <CompanyStep t={t} v={d.company || {}} set={(v) => save("company", v)} />}
            {step === 2 && <MaterialsStep t={t} v={d.materials || []} set={(v) => save("materials", v)} />}
            {step === 3 && <PlotsStep t={t} plots={st.plots} materials={d.materials || []} ro={ro} api={api} lang={lang} />}
            {step === 4 && <DocsStep t={t} docs={st.documents} types={st.doc_types} ro={ro} api={api} />}
            {step === 5 && <DdsStep t={t} v={d.materials || []} set={(v) => save("materials", v)} />}
            {step === 5 && st.supplier?.eu_dispatch && <PortalMillRefs base={base} lang={lang} materials={d.materials || []} ro={ro} />}
            {step === 6 && <DownstreamStep t={t} v={d.materials || []} set={(v) => save("materials", v)} />}
            {step === 7 && (
              <div className="space-y-5">
                {st.prefill?.status === "accepted" && <Changes t={t} base={base} />}
                <p className="text-sm text-slate-600">{t.declaration_intro}</p>
                <div className={cn("rounded-xl border p-4", missing.length ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50")} data-testid="portal-missing-list">
                  {missing.length ? (
                    <><p className="mb-2 flex items-center gap-2 font-semibold text-amber-900"><AlertTriangle className="h-4 w-4" />{t.missing_title}</p>
                      <ul className="list-inside list-disc space-y-1 text-sm text-amber-900">{missing.map((m, i) => <li key={i}>{missingText(t, m)}</li>)}</ul></>
                  ) : <p className="flex items-center gap-2 font-semibold text-emerald-800"><Check className="h-4 w-4" />{t.all_complete}</p>}
                </div>
                <label className="flex items-start gap-3 rounded-xl border border-slate-200 p-4 text-sm text-slate-700">
                  <input type="checkbox" className="mt-1 h-4 w-4" data-testid="portal-declaration-checkbox" checked={!!d.declaration} onChange={(e) => save("declaration", e.target.checked, 0)} />
                  {t.declaration_text}
                </label>
                <label className="block"><span className="mb-1.5 block text-[13px] font-semibold text-slate-700">{t.signed_name}</span>
                  <input data-testid="portal-signed-name" className={inputCls} value={sign} onChange={(e) => setSign(e.target.value)} /></label>
                {submitErr && (
                  <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" data-testid="portal-submit-error">
                    <p className="font-semibold">{t.missing_title}</p><ul className="mt-1 list-inside list-disc">{submitErr.map((m, i) => <li key={i}>{m.key === "error" ? t.error : missingText(t, m)}</li>)}</ul>
                  </div>
                )}
                {!ro && <Btn data-testid="portal-submit-btn" onClick={submit} disabled={busy} className="w-full sm:w-auto">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{busy ? t.submitting : t.submit}</Btn>}
              </div>
            )}
          </fieldset>
          {!ro && <HelpTip t={t} step={step} helpEmail={st.help_email} />}
          <div className="mt-8 flex justify-between gap-3 border-t border-slate-100 pt-4">
            <Btn variant="outline" disabled={!prev} onClick={() => go(prev)} data-testid="portal-back-btn">{t.back}</Btn>
            {nxt && <Btn onClick={() => go(nxt)} data-testid="portal-next-btn">{t.next}</Btn>}
          </div>
        </section>
        <p className="mt-8 text-center text-xs text-slate-400">{t.footer}</p>
      </main>
    </div>
  );
}
